// Event-handler factory for the QE/TE map view (T13). Split out of `SpatialResultsMapPanel.tsx` so
// that file stays under the 200-line cap (T31) — pure wiring, no new behavior: each handler mirrors
// exactly what previously lived inline in the panel (track + state-setter, or a plain forward).

import { useCallback } from 'react';
import type { TrackFunction } from '../../../../core/analytics';

export type Hover = { id: string | number; x: number; y: number } | null;

export function useSpatialResultsMapHandlers(params: {
  track: TrackFunction;
  setGeometryColumnId: (id: string) => void;
  setColorByColumnId: (id: string | null) => void;
  setHover: (hover: Hover) => void;
  onRowSelect?: (rowIndex: number) => void;
  /** T29 — the panel's own container, so `handleContainerMouseDown` can tell a real DOM
   *  descendant from a portalled one (see that handler's doc comment). */
  containerRef: React.RefObject<HTMLElement | null>;
}) {
  const { track, setGeometryColumnId, setColorByColumnId, setHover, onRowSelect, containerRef } = params;

  const handleGeometryChange = useCallback(
    (columnId: string) => {
      setGeometryColumnId(columnId);
      setHover(null);
      track('feature:spatial_map_config', { control: 'geometry-column' });
    },
    [track, setGeometryColumnId, setHover],
  );
  const handleColorByChange = useCallback(
    (columnId: string | null) => {
      setColorByColumnId(columnId);
      track('feature:spatial_map_config', { control: 'color-by' });
    },
    [track, setColorByColumnId],
  );
  const handleFeatureClick = useCallback(
    (_layerId: string, featureId: string | number) => {
      const idx = Number(featureId);
      if (Number.isFinite(idx)) onRowSelect?.(idx);
    },
    [onRowSelect],
  );
  const handleHover = useCallback(
    (_layerId: string, featureId: string | number | null, point: { x: number; y: number }) => {
      setHover(featureId === null ? null : { id: featureId, x: point.x, y: point.y });
    },
    [setHover],
  );
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') setHover(null);
    },
    [setHover],
  );
  // T29 fix: only refocus the panel for a mousedown whose target is an ACTUAL DOM descendant of
  // it. A portalled popover (e.g. `SpatialNudgeChip`'s "swap axes" toggle) is a REACT descendant —
  // React re-fires bubbled/captured events along the fiber tree regardless of portal placement, so
  // this handler still runs for it — but it is NOT a DOM descendant of `containerRef.current`.
  // Unconditionally refocusing on every mousedown made Radix's dismissable layer see that refocus
  // as a "focus outside" interaction and close the popover before its own click could register.
  const handleContainerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) containerRef.current.focus();
    },
    [containerRef],
  );

  return {
    handleGeometryChange,
    handleColorByChange,
    handleFeatureClick,
    handleHover,
    handleKeyDown,
    handleContainerMouseDown,
  };
}
